import React, { useState, useEffect, useCallback } from 'react';

// LIBS
import { debounce } from 'lodash';

// STYLES
import { CircularProgress, Autocomplete, TextField } from '@mui/material';

// APIS
import { apiListEstablishment } from 'js/library/utils/API/Establishment/apiListEstablishments';
import { findStoreByProximity } from 'js/library/utils/API/Store/findStoreByProximity';
import { getEstablishment } from 'js/library/utils/API/getEstablishment';
import getPromotionInBatch from 'js/library/utils/API/Store/getPromotionInBatch';

export default function AutocompleteListType({
  typeCard,
  loading,
  setLoading,
  selectedEstablishment,
  setSelectedEstablishment,
  selectedStore,
  setSelectedStore,
  selectedProduct,
  setSelectedProduct,
  selectedIdEdit,
}) {
  const [dataEstablishment, setDataEstablishment] = useState([]);
  const [dataStore, setDataStore] = useState([]);
  const [dataProduct, setDataProduct] = useState([]);

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState('');

  const loadSelectedItem = useCallback(async () => {
    if (!selectedIdEdit) return;

    try {
      setLoading(true);

      switch (typeCard) {
        case 'establishment_list':
          const establishment = await getEstablishment(selectedIdEdit);
          setSelectedEstablishment(establishment);
          setTextFieldValue(establishment.nome || '');
          break;

        case 'promotion_list':
        case 'product_list':
          const arraySelectedIdEdit = [selectedIdEdit];
          const promotion = await getPromotionInBatch(arraySelectedIdEdit);
          if (typeCard === 'promotion_list') {
            setSelectedStore(promotion.response.storeList[0]);
            setTextFieldValue(promotion.response.storeList[0].title || '');
          } else {
            setSelectedProduct(promotion.response.storeList[0]);
            setTextFieldValue(promotion.response.storeList[0].title || '');
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Erro ao carregar item selecionado:', error);
    } finally {
      setLoading(false);
    }
  }, [
    selectedIdEdit,
    typeCard,
    setLoading,
    setSelectedEstablishment,
    setSelectedStore,
    setSelectedProduct,
  ]);

  useEffect(() => {
    if (typeCard === 'promotion_list') {
      setDataEstablishment([]);
      setSelectedEstablishment(null);
    } else if (typeCard === 'establishment_list') {
      setDataStore([]);
      setSelectedStore(null);
    }
    setTextFieldValue('');
  }, [typeCard]);

  useEffect(() => {
    if (selectedIdEdit) {
      loadSelectedItem();
    } else {
      // Reset quando não há ID selecionado
      setTextFieldValue('');
      if (typeCard === 'promotion_list') {
        setSelectedEstablishment(null);
        setDataEstablishment([]);
      } else if (typeCard === 'establishment_list') {
        setSelectedStore(null);
        setDataStore([]);
      }
    }
  }, [selectedIdEdit, typeCard, loadSelectedItem]);

  const labelType = () => {
    switch (typeCard) {
      case 'establishment_list':
        return 'Selecione um estabelecimento';
      case 'promotion_list':
        return 'Selecione uma promoção';
      case 'product_list':
        return 'Selecione um produto';
      default:
        break;
    }
  };

  const optionsAutoComplete = () => {
    switch (typeCard) {
      case 'establishment_list':
        return dataEstablishment;
      case 'promotion_list':
        return dataStore;
      case 'product_list':
        return dataProduct;
      default:
        break;
    }
  };

  // dados dos tipos para o autoComplete
  const fetchData = async (query = '', from = 0, size = 10) => {
    const payloadStore = {
      title: query,
      isPublished: true,
      from: from,
      size: size,
    };

    const payloadProduct = {
      title: query,
      isPublished: true,
      from: from,
      size: 5000,
    };

    try {
      let establishmentData;
      let storeData;
      let productData;

      switch (typeCard) {
        case 'establishment_list':
          // estabelecimentos
          establishmentData = await apiListEstablishment(query, from, size);
          setDataEstablishment(establishmentData);
          break;
        case 'promotion_list':
          // promoçoões
          storeData = await findStoreByProximity(payloadStore);
          setDataStore(storeData);
          break;
        case 'product_list':
          // produtos
          productData = await findStoreByProximity(payloadProduct);
          const filteredProducts = productData.filter((item) => item.type === 'product');
          setDataProduct(filteredProducts);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Ocorreu um erro na requisição:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [typeCard]);

  const performFetch = async (query) => {
    setLoading(true);

    // Se o valor digitado não estiver na lista inicial, fazer nova requisição (estabelecimento e promoção)
    if (
      (!dataEstablishment.some(
        (establishment) => establishment.nome.toLowerCase() === query.toLowerCase()
      ) &&
        query.length >= 2) ||
      (!dataStore.some((store) => store.title.toLowerCase() === query.toLowerCase()) &&
        query.length >= 2)
    ) {
      await fetchData(query, 0, 10);
      setLoading(false);
    }
  };

  // Função debounce utilizando lodash
  const debouncedFetch = useCallback(
    debounce(async (query) => {
      await performFetch(query);
    }, 1000),
    [dataEstablishment, dataStore]
  );

  useEffect(() => {
    debouncedFetch(textFieldValue.trim());
  }, [textFieldValue]);

  const handleChange = (event, value) => {
    if (value) {
      const selectedId = value.id;

      // Filtrando os dados do estabelecimento com base no id selecionado
      const filteredDataEstablishment = dataEstablishment.filter((item) => item.id === selectedId);
      setSelectedEstablishment(
        filteredDataEstablishment.length > 0 ? filteredDataEstablishment[0] : null
      );

      // Filtrando os dados da promoção com base no id selecionado
      const filteredDataStore = dataStore.filter((item) => item.id === selectedId);
      setSelectedStore(filteredDataStore.length > 0 ? filteredDataStore[0] : null);

      // Filtrando os dados do produto com base no id selecionado
      const filteredDataProduct = dataProduct.filter((item) => item.id === selectedId);
      setSelectedProduct(filteredDataProduct.length > 0 ? filteredDataProduct[0] : null);
    } else {
      // Se nada for selecionado
      setSelectedEstablishment(null);
      setSelectedStore(null);
      setSelectedProduct(null);
    }
  };

  return (
    <Autocomplete
      disablePortal
      id="combo-box-demo"
      options={optionsAutoComplete()}
      getOptionLabel={(option) => option?.nome || option?.title || ''}
      value={
        typeCard === 'establishment_list'
          ? selectedEstablishment
          : typeCard === 'promotion_list'
          ? selectedStore
          : typeCard === 'product_list'
          ? selectedProduct
          : null
      }
      fullWidth
      blurOnSelect
      noOptionsText={loading ? <CircularProgress size={15} /> : 'Nenhum resultado encontrado...'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={labelType()}
          onClick={() => setOptionsOpen(true)}
          onFocus={() => setOptionsOpen(true)}
          onBlur={() => setOptionsOpen(false)}
        />
      )}
      ListboxProps={{ style: { maxHeight: '200px' } }}
      open={optionsOpen}
      onClose={() => setOptionsOpen(false)}
      onChange={handleChange}
      onInputChange={(event, newInputValue) => {
        setTextFieldValue(newInputValue);
        if (newInputValue === '') {
          fetchData('', 0, 10);
        }
      }}
    />
  );
}
